/**
 * A KIJELÖLÉS ÉS A BEKÉRÉS — a tracer bullet 4. lépése („a könyvelő `Enter`-t
 * nyom", `m0b-build-terv.md` §8c).
 *
 * A MŰVELET EGYSÉGE A CÉG, NEM A SOR. Ez a `hasznalati-esetek.md` UC1 6–7.
 * lépéséből jön szó szerint: *„a bekérhetőket KIJELÖLVE HAGYJA"*, majd
 * *„kiadja a bekérést AZ ÜGYFÉLRE"*. Két dolgot mond ki:
 *
 *   1. **Alapból minden bekérhető tétel ki van jelölve.** A könyvelő nem
 *      gyűjt, hanem dönt — a munkája a KIVÉTELEK levétele, nem a rutin
 *      összeszedése. Ha üresen indulnánk, minden hónapban ugyanazt a több
 *      száz kattintást kérnénk tőle, amiért az egész rendszer van.
 *   2. **Az `Enter` egy CÉGRE ad ki bekérést**, nem egy sorra. Soronként
 *      kiadva ugyanaz az ügyfél egy kör alatt több levelet kapna, és az
 *      pontosan a 2.4d bukásmódja: *„havi 12 külön levél ugyanannak az
 *      ügyfélnek → kikapcsolja"*. A szerver ugyan cégenként csoportosít, de
 *      csak azon a kijelölésen belül, amit EGY kérésben lát.
 *
 * MI NEM JELÖLHETŐ KI, és miért nem csak „nincs kipipálva":
 *
 *   • `WAITING` — nem hiány. A papír talán a feldolgozatlan kupacban van.
 *   • `REQUESTED` — már kiment. A második kiadás `ALREADY_REQUESTED`-et adna;
 *     az emlékeztető külön mechanizmus lesz, nem ugyanannak a gombnak a
 *     kétszeri megnyomása.
 *
 * MIÉRT KÜLÖN HOROG a `useDiscrepancyList`-től: az egyik OLVAS, a másik ÍR.
 * A kettőnek más az élettartama és más a hibakezelése — egy elbukott bekérés
 * nem teszi tönkre a listát, és egy elbukott betöltés után nincs mit kiadni.
 * A `Shell` fűzi össze a rövidítéseiket egy regiszterbe.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Shortcut } from '@/hooks/useShortcuts';
import { ApiError, api } from '@/lib/api-client';
import type { DiscrepancyList, DiscrepancyRequestResult, SkipReason } from './types';
import { companyIdOfRow, type DiscrepancyListState, groupRows } from './useDiscrepancyList';

/** Amit a kiadás után mondunk. A hiba HÁROM ága három külön teendő. */
export type RequestFeedback =
  | { kind: 'done'; company: string; count: number; skipped: SkipReason[] }
  /** Nincs mit kiadni — nem hiba, csak nincs kijelölve semmi. */
  | { kind: 'empty' }
  /**
   * A kijelölés valamelyik sorát a szerver nem látja (404). Ilyenkor SEMMI
   * nem ment ki, és a lista elavult — újratöltjük.
   */
  | { kind: 'stale' }
  | { kind: 'forbidden' }
  /**
   * A KÉRÉSSEL van baj, nem a rendszerrel (400) — például a kijelölés
   * túllépi a szerver egyszerre feldolgozható korlátját (ma 200 tétel).
   *
   * EZ AZ EGYETLEN ÁG, AHOL A SZERVER ÜZENETÉT MUTATJUK, és ez nem
   * következetlenség: a 400 arról szól, amit a FELHASZNÁLÓ küldött, tehát
   * neki szól és cselekvésre váltható („Egyszerre legfeljebb 200 tétel
   * kérhető be."). Az 500 a rendszerről szól — ott a saját, rövid szövegünk
   * megy. Ugyanez a bontás áll a belépésnél is.
   */
  | { kind: 'rejected'; message: string }
  | { kind: 'failed' };

/**
 * EGY CÉG KIJELÖLTSÉGE — a fejléc jelölőnégyzetének három állapota (#49).
 *
 * A `some` NEM „félig kipipálva": az `indeterminate` a HTML saját állapota, és
 * a képernyőolvasó is kimondja („részben jelölt"). Enélkül a részleges
 * kijelölés vagy üresnek, vagy teljesnek látszana — és a következő kattintás
 * mást tenne, mint amit a könyvelő vár.
 */
export type CompanySelection = 'none' | 'some' | 'all';

export interface DiscrepancySelectionState {
  selected: ReadonlySet<string>;
  /** Kijelölhető-e egyáltalán ez a sor. */
  isSelectable: (id: string) => boolean;
  toggle: (id: string) => void;
  /** Hány kijelölt tétel van ennél a cégnél — a gomb feliratához. */
  selectedCountFor: (companyId: string) => number;
  /** Hány kijelölhető tétele VAN a cégnek — a „mindet" viszonyítási pontja. */
  selectableCountFor: (companyId: string) => number;
  /** A cégfejléc jelölőnégyzetének állapota. */
  selectionFor: (companyId: string) => CompanySelection;
  /**
   * MINDET KI VAGY BE ennél a cégnél. Ha akár egy tétele kijelöletlen, a
   * mozdulat KIJELÖL — a „mindet" a teljes állapot felé visz, és csak a
   * teljesből vesz el. Váltóként az első kattintás részleges kijelölésnél
   * mindent LESZEDNE, pont azt, amit a könyvelő épp összeválogatott.
   */
  toggleCompany: (companyId: string) => void;
  /**
   * Ugyanez tetszőleges tételkörre — a PARTNERFEJLÉC jelölőnégyzete (#77).
   * Ugyanaz a szabály: teljesből le, minden más állapotból fel.
   */
  selectionForIds: (ids: readonly string[]) => CompanySelection;
  toggleIds: (ids: readonly string[]) => void;
  /**
   * BEKÉRÉS. Alapból a cég KIJELÖLT tételeire; `ids` megadva pontosan
   * azokra — ez a soronkénti bekérés útja.
   */
  request: (companyId: string, ids?: string[]) => void;
  sending: boolean;
  feedback: RequestFeedback | null;
  shortcuts: Shortcut[];
}

/** Csak a bekérhető tétel megy ki. A `WAITING` nem hiány, a `REQUESTED` már kiment. */
function selectableIds(list: DiscrepancyList | null): Set<string> {
  const ids = new Set<string>();
  for (const group of list?.groups ?? []) {
    for (const item of group.items) {
      if (item.state === 'OPEN') {
        ids.add(item.id);
      }
    }
  }
  return ids;
}

/**
 * A PARTNERFEJLÉCEN a `Space` a partner összes tételére szól (#77) — ugyanaz az
 * egy jelentés, mint a cégfejlécen: „a kijelölés váltása azon, amin állok".
 * Kijelölhető tétel híján nincs bejegyezve, hogy a lapgörgetést ne vegye el.
 */
function partnerShortcuts(
  ids: readonly string[] | undefined,
  selectable: ReadonlySet<string>,
  toggleIds: (ids: readonly string[]) => void
): Shortcut[] {
  if (!ids?.some((id) => selectable.has(id))) {
    return [];
  }
  return [{ key: ' ', labelKey: 'shortcut.selectPartner', run: () => toggleIds(ids) }];
}

export function useDiscrepancySelection(list: DiscrepancyListState): DiscrepancySelectionState {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<RequestFeedback | null>(null);

  const selectable = useMemo(() => selectableIds(list.list), [list.list]);

  /**
   * ÚJ LISTA = ÚJ ALAPKIJELÖLÉS. Minden bekérhető tétel ki van jelölve —
   * a kivételek levétele a könyvelő munkája, nem a rutin összeszedése.
   *
   * A korábbi kijelölést SZÁNDÉKOSAN nem őrizzük meg: egy kiadás után a
   * frissített listán a most kiküldött sorok már `REQUESTED`-ek, tehát nem
   * is jelölhetők — egy megőrzött halmaz ott csak félrevezetne.
   */
  useEffect(() => {
    setSelected(new Set(selectable));
  }, [selectable]);

  const toggle = useCallback(
    (id: string) => {
      if (!selectable.has(id)) {
        return;
      }
      setSelected((previous) => {
        const next = new Set(previous);
        if (!next.delete(id)) {
          next.add(id);
        }
        return next;
      });
    },
    [selectable]
  );

  /** Cég → a benne lévő tételek azonosítói. A kiadás ebből dolgozik. */
  const byCompany = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of list.list?.groups ?? []) {
      map.set(
        group.company.id,
        group.items.map((item) => item.id)
      );
    }
    return map;
  }, [list.list]);

  /**
   * Sor → cég, a PARTNERFEJLÉCEKKEL együtt (#77): a fejlécen álló `Enter`
   * ugyanúgy a cég bekérését adja ki, mint a tételen álló.
   */
  const companyOf = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const group of list.list?.groups ?? []) {
      for (const row of groupRows(group)) {
        map.set(row.rowId, group.company);
      }
    }
    return map;
  }, [list.list]);

  /** Partnerfejléc → a blokk tételei. */
  const partnerItems = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const group of list.list?.groups ?? []) {
      for (const row of groupRows(group)) {
        if (row.kind === 'partner') {
          map.set(row.rowId, row.partner.itemIds);
        }
      }
    }
    return map;
  }, [list.list]);

  const selectedCountFor = useCallback(
    (companyId: string) => (byCompany.get(companyId) ?? []).filter((id) => selected.has(id)).length,
    [byCompany, selected]
  );

  /**
   * A CÉG KIJELÖLHETŐ TÉTELEI — a „mindet ki/be" alapja.
   *
   * ⚠️ NEM a cég ÖSSZES tétele. A `WAITING` nem hiány, a `REQUESTED` már
   * kiment; egy „mindet" mozdulat, ami ezekre is rátenné a pipát, olyasmit
   * ígérne, amit a kiadás úgyis eldobna (`ALREADY_REQUESTED`).
   */
  const selectableOf = useCallback(
    (companyId: string) => (byCompany.get(companyId) ?? []).filter((id) => selectable.has(id)),
    [byCompany, selectable]
  );

  const selectableCountFor = useCallback(
    (companyId: string) => selectableOf(companyId).length,
    [selectableOf]
  );

  const selectionForIds = useCallback(
    (all: readonly string[]): CompanySelection => {
      const ids = all.filter((id) => selectable.has(id));
      if (ids.length === 0) {
        return 'none';
      }
      const picked = ids.filter((id) => selected.has(id)).length;
      if (picked === 0) {
        return 'none';
      }
      return picked === ids.length ? 'all' : 'some';
    },
    [selectable, selected]
  );

  const selectionFor = useCallback(
    (companyId: string) => selectionForIds(byCompany.get(companyId) ?? []),
    [byCompany, selectionForIds]
  );

  const toggleIds = useCallback(
    (all: readonly string[]) => {
      const ids = all.filter((id) => selectable.has(id));
      if (ids.length === 0) {
        return;
      }
      // TELJESBŐL LE, minden más állapotból FEL — lásd a típus fejlécét.
      const clearing = ids.every((id) => selected.has(id));
      setSelected((previous) => {
        const next = new Set(previous);
        for (const id of ids) {
          if (clearing) {
            next.delete(id);
          } else {
            next.add(id);
          }
        }
        return next;
      });
    },
    [selectable, selected]
  );

  const toggleCompany = useCallback(
    (companyId: string) => toggleIds(byCompany.get(companyId) ?? []),
    [byCompany, toggleIds]
  );

  const request = useCallback(
    (companyId: string, only?: string[]) => {
      if (sending) {
        return;
      }
      /**
       * EGY SORT IS BE LEHET KÉRNI (#49, Viktor 2026-09-18).
       *
       * A szerver ezt eddig is tudta — a `discrepancyIds` tömb egy elemmel is
       * érvényes —, csak a felület nem adott rá utat: egyetlen számlához a
       * könyvelőnek le kellett szednie a pipát a cég összes többi tételéről.
       *
       * ⚠️ A SZŰRÉS ITT IS A KIJELÖLHETŐSÉGEN MEGY, nem a kijelöltségen. A
       * soronkénti bekérés szándékosan FÜGGETLEN a pipáktól — az a mozdulat,
       * hogy „ezt az egyet most kérem be", nem az, hogy „a kijelölésből ezt".
       */
      const ids = (
        only ?? (byCompany.get(companyId) ?? []).filter((id) => selected.has(id))
      ).filter((id) => selectable.has(id));
      if (ids.length === 0) {
        setFeedback({ kind: 'empty' });
        return;
      }

      const company = list.list?.groups.find((group) => group.company.id === companyId);
      setSending(true);
      setFeedback(null);

      void api<DiscrepancyRequestResult>('/discrepancies/request', {
        method: 'POST',
        body: JSON.stringify({ discrepancyIds: ids }),
      })
        .then((result) => {
          setFeedback({
            kind: 'done',
            company: company?.company.name ?? '',
            count: result.requested.length,
            skipped: result.skipped.map((entry) => entry.reason),
          });
          // A kiadott sorok `REQUESTED`-be léptek — a lista újratöltése az
          // egyetlen olyan frissítés, ami nem a szerver állapotát TIPPELI meg.
          list.reload();
        })
        .catch((caught: unknown) => {
          const status = caught instanceof ApiError ? caught.status : 0;

          if (status === 404) {
            // A kijelölés egy sorát a szerver nem látja. Ilyenkor SEMMI nem
            // ment ki — a lista elavult, nem a kérés volt rossz.
            setFeedback({ kind: 'stale' });
            list.reload();
            return;
          }
          if (status === 403) {
            setFeedback({ kind: 'forbidden' });
            return;
          }
          if (status === 400 && caught instanceof ApiError && caught.message) {
            setFeedback({ kind: 'rejected', message: caught.message });
            return;
          }
          setFeedback({ kind: 'failed' });
        })
        .finally(() => setSending(false));
    },
    [byCompany, selected, selectable, sending, list]
  );

  /**
   * A FÓKUSZÁLT SOR CÉGÉRE ad ki bekérést — ezért kell a `focusedId`.
   * Billentyűzet-elsőn a kéz a listán van; a cégválasztás nem külön lépés,
   * hanem az, hogy hol áll a kurzor.
   */
  const shortcuts = useMemo<Shortcut[]>(() => {
    const focused = list.focusedId;
    if (focused === null) {
      return [];
    }

    const entries: Shortcut[] = [];

    /**
     * A CÉGFEJLÉCEN UGYANAZ A BILLENTYŰ AZ EGÉSZ CÉGRE SZÓL (#49).
     *
     * A `Space` a fejlécen „mindet ki/be", a tételen egyetlen sort vált — és
     * ez nem két jelentés, hanem EGY: „a kijelölés váltása azon, amin állok".
     * A hatókört a kurzor helye adja, ahogy az `Enter`-nél is (a cég a
     * kurzorból jön, nem külön választásból).
     */
    const headerCompany = companyIdOfRow(focused);
    if (headerCompany !== null) {
      if (selectableCountFor(headerCompany) > 0) {
        entries.push({
          key: ' ',
          labelKey: 'shortcut.selectCompany',
          run: () => toggleCompany(headerCompany),
        });
      }
      entries.push({
        key: 'Enter',
        labelKey: 'shortcut.request',
        run: () => request(headerCompany),
      });
      return entries;
    }

    if (selectable.has(focused)) {
      entries.push({ key: ' ', labelKey: 'shortcut.select', run: () => toggle(focused) });
    }

    entries.push(...partnerShortcuts(partnerItems.get(focused), selectable, toggleIds));

    const company = companyOf.get(focused);
    if (company) {
      entries.push({
        key: 'Enter',
        labelKey: 'shortcut.request',
        run: () => request(company.id),
      });
      // CSAK EZT A SORT — az `Enter` szűkebb alakja. A kijelöléstől
      // függetlenül megy ki; lásd a `request` fejlécét.
      if (selectable.has(focused)) {
        entries.push({
          key: 'Enter',
          shift: true,
          labelKey: 'shortcut.requestRow',
          run: () => request(company.id, [focused]),
        });
      }
    }
    return entries;
  }, [
    list.focusedId,
    selectable,
    companyOf,
    partnerItems,
    toggle,
    toggleIds,
    toggleCompany,
    selectableCountFor,
    request,
  ]);

  return {
    selected,
    isSelectable: useCallback((id: string) => selectable.has(id), [selectable]),
    toggle,
    selectedCountFor,
    selectableCountFor,
    selectionFor,
    toggleCompany,
    selectionForIds,
    toggleIds,
    request,
    sending,
    feedback,
    shortcuts,
  };
}
